import "@babylonjs/core/Culling/ray";
import { Material } from "@babylonjs/core/Materials/material";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Viewport } from "@babylonjs/core/Maths/math.viewport";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { Scene } from "@babylonjs/core/scene";
import type { Disposable } from "@/shared/contracts/lifecycle";
import {
  FAIRY_GLOW_SCALE,
  FAIRY_SIZE_MAX,
  type FairyBounds,
  type FairyId,
  type FairySnapshot,
} from "./fairyModel";

interface FairyMeshEntry {
  readonly mesh: Mesh;
  readonly material: StandardMaterial;
  readonly glowMesh: Mesh;
  readonly glowMaterial: StandardMaterial;
  readonly barRoot: HTMLDivElement;
  readonly barFill: HTMLDivElement;

  screenX: number;
  screenY: number;
  hitRadiusPx: number;
}

interface FairyViewOptions {
  readonly scene: Scene;
  readonly canvas: HTMLCanvasElement;
  readonly onHoverChange: (id: FairyId | null) => void;
}

interface FairyRenderExtras {
  readonly size?: number;
  readonly glowSize?: number;
  readonly color?: {
    readonly hue: number;
    readonly saturation: number;
    readonly value: number;
    readonly alpha: number;
  };
  readonly glowAlpha?: number;
  readonly vibrationOffsetX?: number;
  readonly vibrationOffsetY?: number;
}

const FAIRY_MESH_NAME_PREFIX = "fairy-mesh-";
const FAIRY_GLOW_MESH_NAME_PREFIX = "fairy-glow-";

const DEFAULT_FAIRY_SIZE_PX = 22;
const DEFAULT_FAIRY_GLOW_SCALE = 2.2;
const DEFAULT_FAIRY_HUE = 0.14;
const DEFAULT_FAIRY_SATURATION = 0.45;
const DEFAULT_FAIRY_VALUE = 1;
const DEFAULT_FAIRY_ALPHA = 0.95;
const DEFAULT_FAIRY_GLOW_ALPHA = 0.25;

const FAIRY_WORLD_SIZE_PER_PIXEL = 0.0015;

const HP_BAR_HEIGHT_PX = 5;
const FAIRY_SAFE_EDGE_MARGIN_PX = 8;
const FAIRY_SAFE_TOP_MARGIN_PX = 8;

export class FairyView implements Disposable {
  private readonly scene: Scene;
  private readonly canvas: HTMLCanvasElement;
  private readonly onHoverChange: (id: FairyId | null) => void;
  private readonly meshes = new Map<FairyId, FairyMeshEntry>();
  private readonly barLayer: HTMLDivElement;
  private readonly identityMatrix = Matrix.Identity();

  private hoveredId: FairyId | null = null;
  private reduceMotion = false;
  private isDisposed = false;
  private isVisible = true;
  private lastPointer: { x: number; y: number } | null = null;

  private readonly handlePointerMove = (event: PointerEvent): void => {
    this.lastPointer = { x: event.clientX, y: event.clientY };
  };

  private readonly handlePointerLeave = (): void => {
    this.lastPointer = null;
  };

  constructor(options: FairyViewOptions) {
    this.scene = options.scene;
    this.canvas = options.canvas;
    this.onHoverChange = options.onHoverChange;

    this.barLayer = this.createBarLayer();

    window.addEventListener("pointermove", this.handlePointerMove);
    window.addEventListener("pointerleave", this.handlePointerLeave);
    window.addEventListener("blur", this.handlePointerLeave);
  }

  setReduceMotion(reduceMotion: boolean): void {
    this.reduceMotion = reduceMotion;
  }

  setVisible(visible: boolean): void {
    if (this.isVisible === visible) return;

    this.isVisible = visible;

    for (const entry of this.meshes.values()) {
      entry.mesh.isVisible = visible;
      entry.mesh.isPickable = visible;
      entry.glowMesh.isVisible = visible;
      entry.glowMesh.isPickable = false;
    }

    this.barLayer.style.display = visible ? "" : "none";

    if (!visible && this.hoveredId !== null) {
      this.hoveredId = null;
      this.onHoverChange(null);
    }
  }

  computeWorldBounds(): FairyBounds | null {
    const camera = this.scene.activeCamera;
    if (!camera) {
      return null;
    }

    const engine = this.scene.getEngine();
    const renderWidth = Math.max(1, engine.getRenderWidth());
    const renderHeight = Math.max(1, engine.getRenderHeight());
    const rect = this.canvas.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }

    const safeRect = this.computeSafeScreenRect(rect);
    if (safeRect === null) {
      return null;
    }

    const topLeft = this.screenPointToWorld(
      safeRect.left,
      safeRect.top,
      rect,
      renderWidth,
      renderHeight,
    );
    const topRight = this.screenPointToWorld(
      safeRect.right,
      safeRect.top,
      rect,
      renderWidth,
      renderHeight,
    );
    const bottomLeft = this.screenPointToWorld(
      safeRect.left,
      safeRect.bottom,
      rect,
      renderWidth,
      renderHeight,
    );
    const bottomRight = this.screenPointToWorld(
      safeRect.right,
      safeRect.bottom,
      rect,
      renderWidth,
      renderHeight,
    );

    if (!topLeft || !topRight || !bottomLeft || !bottomRight) {
      return null;
    }

    const radiusPx = (FAIRY_SIZE_MAX * FAIRY_GLOW_SCALE) / 2;
    const centerX = (safeRect.left + safeRect.right) / 2;
    const centerY = (safeRect.top + safeRect.bottom) / 2;
    const center = this.screenPointToWorld(
      centerX,
      centerY,
      rect,
      renderWidth,
      renderHeight,
    );
    const rightOfCenter = this.screenPointToWorld(
      Math.min(safeRect.right, centerX + 1),
      centerY,
      rect,
      renderWidth,
      renderHeight,
    );
    const belowCenter = this.screenPointToWorld(
      centerX,
      Math.min(safeRect.bottom, centerY + 1),
      rect,
      renderWidth,
      renderHeight,
    );

    if (!center || !rightOfCenter || !belowCenter) {
      return null;
    }

    const worldPerPixelX = Math.abs(rightOfCenter.x - center.x);
    const worldPerPixelY = Math.abs(belowCenter.y - center.y);
    const shrinkX = radiusPx * worldPerPixelX;
    const shrinkY = radiusPx * worldPerPixelY;

    const minX =
      Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x) + shrinkX;
    const maxX =
      Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x) - shrinkX;
    const minY =
      Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y) + shrinkY;
    const maxY =
      Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y) - shrinkY;

    if (minX >= maxX || minY >= maxY) {
      return null;
    }

    return {
      minX,
      maxX,
      minY,
      maxY,
    };
  }

  update(snapshot: FairySnapshot): void {
    if (this.isDisposed) return;

    const seenIds = new Set<FairyId>();

    for (const fairyBase of snapshot.fairies) {
      const fairy = fairyBase as typeof fairyBase & FairyRenderExtras;

      seenIds.add(fairy.id);

      const sizePx = fairy.size ?? DEFAULT_FAIRY_SIZE_PX;

      const entry =
        this.meshes.get(fairy.id) ??
        this.createMesh(fairy.id, fairy.x, fairy.y, sizePx);

      const glowSizePx = fairy.glowSize ?? sizePx * DEFAULT_FAIRY_GLOW_SCALE;

      const worldSize = sizePx * FAIRY_WORLD_SIZE_PER_PIXEL;
      const glowWorldSize = glowSizePx * FAIRY_WORLD_SIZE_PER_PIXEL;

      entry.mesh.scaling.set(worldSize, worldSize, 1);
      entry.glowMesh.scaling.set(glowWorldSize, glowWorldSize, 1);

      const shouldVibrate = !this.reduceMotion && fairy.isHovered;

      const vibrationWorldX =
        shouldVibrate && fairy.vibrationOffsetX !== undefined
          ? fairy.vibrationOffsetX * FAIRY_WORLD_SIZE_PER_PIXEL
          : 0;

      const vibrationWorldY =
        shouldVibrate && fairy.vibrationOffsetY !== undefined
          ? fairy.vibrationOffsetY * FAIRY_WORLD_SIZE_PER_PIXEL
          : 0;

      const displayX = fairy.x + vibrationWorldX;
      const displayY = fairy.y + vibrationWorldY;

      entry.mesh.position.set(displayX, displayY, 0);
      entry.glowMesh.position.set(displayX, displayY, 0.001);

      this.applyColor(entry, fairy);

      this.updateHpBar(entry, displayX, displayY, sizePx, fairy.hoverProgress);
      this.updateHitArea(entry, displayX, displayY, sizePx);
    }

    for (const id of [...this.meshes.keys()]) {
      if (!seenIds.has(id)) {
        this.disposeMesh(id);

        if (this.hoveredId === id) {
          this.hoveredId = null;
          this.onHoverChange(null);
        }
      }
    }

    this.refreshHover();
  }

  dispose(): void {
    if (this.isDisposed) return;

    this.isDisposed = true;

    window.removeEventListener("pointermove", this.handlePointerMove);
    window.removeEventListener("pointerleave", this.handlePointerLeave);
    window.removeEventListener("blur", this.handlePointerLeave);

    for (const id of [...this.meshes.keys()]) {
      this.disposeMesh(id);
    }

    this.barLayer.remove();

    this.hoveredId = null;
  }

  private computeSafeScreenRect(
    canvasRect: DOMRect,
  ): { left: number; top: number; right: number; bottom: number } | null {
    const navWidth = this.resolveNavWidth(canvasRect);
    const topInset = this.resolveTopInset(canvasRect);

    const left = FAIRY_SAFE_EDGE_MARGIN_PX;
    const top = topInset;
    const right = canvasRect.width - navWidth - FAIRY_SAFE_EDGE_MARGIN_PX;
    const bottom = canvasRect.height - FAIRY_SAFE_EDGE_MARGIN_PX;

    if (left >= right || top >= bottom) {
      return null;
    }

    return { left, top, right, bottom };
  }

  private resolveNavWidth(canvasRect: DOMRect): number {
    const nav = document.querySelector<HTMLElement>(".hover-idle-nav");
    if (nav && !nav.hidden) {
      const navRect = nav.getBoundingClientRect();
      if (
        navRect.width > 0 &&
        navRect.right > canvasRect.left &&
        navRect.left < canvasRect.right
      ) {
        return Math.min(navRect.width, canvasRect.width);
      }
    }

    const navWidthValue = getComputedStyle(document.documentElement)
      .getPropertyValue("--nav-width")
      .trim();
    const parsed = Number.parseFloat(navWidthValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  private resolveTopInset(canvasRect: DOMRect): number {
    let topInset = FAIRY_SAFE_EDGE_MARGIN_PX;

    for (const tabs of document.querySelectorAll<HTMLElement>(
      ".hover-idle-core-tabs",
    )) {
      if (!isElementVisible(tabs)) {
        continue;
      }
      const tabsRect = tabs.getBoundingClientRect();
      const overlap =
        tabsRect.bottom - canvasRect.top + FAIRY_SAFE_TOP_MARGIN_PX;
      topInset = Math.max(topInset, overlap);
    }

    return Math.min(topInset, canvasRect.height - FAIRY_SAFE_EDGE_MARGIN_PX);
  }

  private screenPointToWorld(
    localX: number,
    localY: number,
    canvasRect: DOMRect,
    renderWidth: number,
    renderHeight: number,
  ): Vector3 | null {
    const camera = this.scene.activeCamera;
    if (!camera) {
      return null;
    }

    const view = camera.getViewMatrix();
    const projection = camera.getProjectionMatrix();
    const scaleX = renderWidth / canvasRect.width;
    const scaleY = renderHeight / canvasRect.height;
    const screenX = localX * scaleX;
    const screenY = localY * scaleY;

    const near = Vector3.Unproject(
      new Vector3(screenX, screenY, 0),
      renderWidth,
      renderHeight,
      this.identityMatrix,
      view,
      projection,
    );
    const far = Vector3.Unproject(
      new Vector3(screenX, screenY, 1),
      renderWidth,
      renderHeight,
      this.identityMatrix,
      view,
      projection,
    );

    const rayZ = far.z - near.z;
    if (Math.abs(rayZ) < 1e-6) {
      return null;
    }

    const t = -near.z / rayZ;
    if (!Number.isFinite(t)) {
      return null;
    }

    return new Vector3(
      near.x + (far.x - near.x) * t,
      near.y + (far.y - near.y) * t,
      0,
    );
  }

  private createBarLayer(): HTMLDivElement {
    const layer = document.createElement("div");
    layer.className = "fairy-hp-layer";
    layer.style.position = "fixed";
    layer.style.left = "0";
    layer.style.top = "0";
    layer.style.width = "0";
    layer.style.height = "0";
    layer.style.pointerEvents = "none";
    layer.style.zIndex = "9999";
    document.body.appendChild(layer);
    return layer;
  }

  private createMesh(
    id: FairyId,
    x: number,
    y: number,
    sizePx: number,
  ): FairyMeshEntry {
    const glowMesh = CreatePlane(
      `${FAIRY_GLOW_MESH_NAME_PREFIX}${id}`,
      { size: 1, sideOrientation: Mesh.DOUBLESIDE },
      this.scene,
    );
    glowMesh.position = new Vector3(x, y, 0.001);
    glowMesh.isVisible = this.isVisible;
    glowMesh.isPickable = false;

    const glowMaterial = new StandardMaterial(
      `fairy-glow-material-${id}`,
      this.scene,
    );
    glowMaterial.emissiveColor = new Color3(1, 1, 1);
    glowMaterial.diffuseColor = new Color3(1, 1, 1);
    glowMaterial.specularColor = new Color3(0, 0, 0);
    glowMaterial.alpha = DEFAULT_FAIRY_GLOW_ALPHA;
    glowMaterial.transparencyMode = Material.MATERIAL_ALPHABLEND;
    glowMaterial.disableLighting = true;
    glowMesh.material = glowMaterial;

    const mesh = CreatePlane(
      `${FAIRY_MESH_NAME_PREFIX}${id}`,
      { size: 1, sideOrientation: Mesh.DOUBLESIDE },
      this.scene,
    );
    mesh.position = new Vector3(x, y, 0);
    mesh.isVisible = this.isVisible;
    mesh.isPickable = this.isVisible;

    const material = new StandardMaterial(`fairy-material-${id}`, this.scene);
    material.emissiveColor = new Color3(1, 1, 1);
    material.diffuseColor = new Color3(1, 1, 1);
    material.specularColor = new Color3(0, 0, 0);
    material.alpha = DEFAULT_FAIRY_ALPHA;
    material.transparencyMode = Material.MATERIAL_ALPHABLEND;
    material.disableLighting = true;
    mesh.material = material;

    const barRoot = document.createElement("div");
    barRoot.className = "fairy-hp-bar";
    barRoot.style.position = "absolute";
    barRoot.style.width = `${sizePx * 2}px`;
    barRoot.style.height = `${HP_BAR_HEIGHT_PX}px`;
    barRoot.style.transform = "translate(-50%, -100%)";
    barRoot.style.background = "rgba(0, 0, 0, 0.5)";
    barRoot.style.border = "none";
    barRoot.style.borderRadius = "2px";
    barRoot.style.display = "none";
    barRoot.style.overflow = "hidden";

    const barFill = document.createElement("div");
    barFill.className = "fairy-hp-bar__fill";
    barFill.style.height = "100%";
    barFill.style.width = "100%";
    barFill.style.background = "#6bd37a";
    barRoot.appendChild(barFill);
    this.barLayer.appendChild(barRoot);

    const entry: FairyMeshEntry = {
      mesh,
      material,
      glowMesh,
      glowMaterial,
      barRoot,
      barFill,
      screenX: 0,
      screenY: 0,
      hitRadiusPx: DEFAULT_FAIRY_SIZE_PX,
    };

    this.meshes.set(id, entry);
    return entry;
  }

  private disposeMesh(id: FairyId): void {
    const entry = this.meshes.get(id);
    if (!entry) return;

    entry.mesh.dispose();
    entry.material.dispose();
    entry.glowMesh.dispose();
    entry.glowMaterial.dispose();
    entry.barRoot.remove();

    this.meshes.delete(id);
  }

  private applyColor(entry: FairyMeshEntry, fairy: FairyRenderExtras): void {
    const hue = fairy.color?.hue ?? DEFAULT_FAIRY_HUE;
    const saturation = fairy.color?.saturation ?? DEFAULT_FAIRY_SATURATION;
    const value = fairy.color?.value ?? DEFAULT_FAIRY_VALUE;
    const alpha = fairy.color?.alpha ?? DEFAULT_FAIRY_ALPHA;
    const glowAlpha = fairy.glowAlpha ?? DEFAULT_FAIRY_GLOW_ALPHA;

    const color = Color3.FromHSV(hue * 360, saturation, value);

    entry.material.emissiveColor = color;
    entry.material.diffuseColor = color;
    entry.material.alpha = alpha;

    entry.glowMaterial.emissiveColor = color;
    entry.glowMaterial.diffuseColor = color;
    entry.glowMaterial.alpha = glowAlpha;
  }

  private updateHpBar(
    entry: FairyMeshEntry,
    worldX: number,
    worldY: number,
    sizePx: number,
    hoverProgress: number,
  ): void {
    if (!this.isVisible || hoverProgress <= 0) {
      entry.barRoot.style.display = "none";
      return;
    }

    const camera = this.scene.activeCamera;
    const engine = this.scene.getEngine();

    if (!camera) {
      entry.barRoot.style.display = "none";
      return;
    }

    const renderWidth = Math.max(1, engine.getRenderWidth());
    const renderHeight = Math.max(1, engine.getRenderHeight());

    const screen = Vector3.Project(
      new Vector3(worldX, worldY, 0),
      this.identityMatrix,
      this.scene.getTransformMatrix(),
      new Viewport(0, 0, renderWidth, renderHeight),
    );

    const rect = this.canvas.getBoundingClientRect();
    const scaleX = rect.width / renderWidth;
    const scaleY = rect.height / renderHeight;

    const left = rect.left + screen.x * scaleX;
    const top = rect.top + screen.y * scaleY - sizePx * 1.2;

    entry.barRoot.style.display = "";
    entry.barRoot.style.left = `${left}px`;
    entry.barRoot.style.top = `${top}px`;

    const hpRatio = Math.max(0, Math.min(1, 1 - hoverProgress));
    entry.barFill.style.width = `${hpRatio * 100}%`;
  }

  private updateHitArea(
    entry: FairyMeshEntry,
    worldX: number,
    worldY: number,
    sizePx: number,
  ): void {
    const camera = this.scene.activeCamera;
    const engine = this.scene.getEngine();

    if (!camera) {
      entry.hitRadiusPx = 0;
      return;
    }

    const renderWidth = Math.max(1, engine.getRenderWidth());
    const renderHeight = Math.max(1, engine.getRenderHeight());

    const screen = Vector3.Project(
      new Vector3(worldX, worldY, 0),
      this.identityMatrix,
      this.scene.getTransformMatrix(),
      new Viewport(0, 0, renderWidth, renderHeight),
    );

    const rect = this.canvas.getBoundingClientRect();
    const scaleX = rect.width / renderWidth;
    const scaleY = rect.height / renderHeight;

    entry.screenX = rect.left + screen.x * scaleX;
    entry.screenY = rect.top + screen.y * scaleY;

    entry.hitRadiusPx = Math.max(28, sizePx * 1.25);
  }

  private refreshHover(): void {
    if (!this.isVisible || this.lastPointer === null) {
      this.updateHovered(null);
      return;
    }

    const rect = this.canvas.getBoundingClientRect();

    if (
      this.lastPointer.x < rect.left ||
      this.lastPointer.y < rect.top ||
      this.lastPointer.x > rect.right ||
      this.lastPointer.y > rect.bottom
    ) {
      this.updateHovered(null);
      return;
    }

    let nearestId: FairyId | null = null;
    let nearestDistanceSq = Number.POSITIVE_INFINITY;

    for (const [id, entry] of this.meshes) {
      if (!entry.mesh.isVisible) continue;

      const dx = this.lastPointer.x - entry.screenX;
      const dy = this.lastPointer.y - entry.screenY;
      const distanceSq = dx * dx + dy * dy;
      const radius = entry.hitRadiusPx;

      if (distanceSq <= radius * radius && distanceSq < nearestDistanceSq) {
        nearestId = id;
        nearestDistanceSq = distanceSq;
      }
    }

    this.updateHovered(nearestId);
  }

  private updateHovered(nextId: FairyId | null): void {
    if (nextId === this.hoveredId) return;

    this.hoveredId = nextId;
    this.onHoverChange(nextId);
  }
}

function isElementVisible(element: HTMLElement): boolean {
  if (element.hidden) {
    return false;
  }
  const style = getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") {
    return false;
  }
  return (
    element.getBoundingClientRect().width > 0 &&
    element.getBoundingClientRect().height > 0
  );
}
